import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { GerencialAccessRestricted } from './components/GerencialAccessRestricted'
import { GerencialTopBar } from './components/GerencialTopBar'
import { GerencialFiltersBar, type GerencialFilterState } from './components/GerencialFiltersBar'
import { GerencialFilterChips, type FilterChip } from './components/GerencialFilterChips'
import { GerencialTable, type GerencialRow, type SortState } from './components/GerencialTable'
import { GerencialEmptyState } from './components/GerencialEmptyState'
import { GerencialEditToolbar } from './components/GerencialEditToolbar'
import { GerencialSaveConfirm } from './components/GerencialSaveConfirm'
import { GerencialSavingDialog } from './components/GerencialSavingDialog'
import { GerencialImportModal } from './components/GerencialImportModal'
import { AuditHistoryDialog } from './components/audit/AuditHistoryDialog'
import {
  useFluxoGerencialListReport,
  useFluxoGerencialClients,
  useFluxoGerencialReport,
  type ListReportFilters,
} from './hooks/useFluxoGerencial'
import {
  useBatchUpdateFluxoGerencial,
  type BatchUpdateFields,
} from './hooks/useFluxoGerencialMutations'
import { useDirtyRows, type EditableField, type CellValue } from './hooks/useDirtyRows'
import { exportGerencialToExcel, downloadBlankTemplate } from './utils/exportToExcel'
import { toDateInputValue, parseDateInputValue } from './utils/dateUtils'

const ALLOWED_ROLES = ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro']
const EDIT_ROLES = ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro']

function getDefaultDateRange() {
  // UTC-anchored to match backend storage. `now` is used only to derive
  // year/month — the actual Date objects are UTC midnight of the target days.
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  return {
    dataInicial: new Date(Date.UTC(year, 0, 1)),
    // Last day of current month: `Date.UTC(year, month + 1, 0)` rolls back to last day of `month`.
    dataFinal: new Date(Date.UTC(year, month + 1, 0)),
  }
}

function buildChips(
  filters: GerencialFilterState,
  clients: Array<{ codigo: string; nome: string }>,
  onClear: (key: keyof GerencialFilterState) => void,
): FilterChip[] {
  const chips: FilterChip[] = []
  if (filters.codigoCliente) {
    const client = clients.find(c => c.codigo === filters.codigoCliente)
    chips.push({
      key: 'cliente',
      label: 'Cliente',
      value: client?.nome ?? filters.codigoCliente,
      onRemove: () => onClear('codigoCliente'),
    })
  }
  if (filters.natureza) {
    chips.push({ key: 'natureza', label: 'Natureza', value: filters.natureza, onRemove: () => onClear('natureza') })
  }
  if (filters.centroResultado) {
    chips.push({ key: 'cr', label: 'C. Resultado', value: filters.centroResultado, onRemove: () => onClear('centroResultado') })
  }
  if (filters.empresa) {
    chips.push({ key: 'empresa', label: 'Empresa', value: filters.empresa, onRemove: () => onClear('empresa') })
  }
  if (filters.centroCusto) {
    chips.push({ key: 'cc', label: 'C. Custo', value: filters.centroCusto, onRemove: () => onClear('centroCusto') })
  }
  if (filters.search) {
    chips.push({ key: 'search', label: 'Busca', value: filters.search, onRemove: () => onClear('search') })
  }
  if (filters.valorMin !== undefined) {
    chips.push({
      key: 'vmin', label: 'Min', value: `R$ ${filters.valorMin.toLocaleString('pt-BR')}`,
      onRemove: () => onClear('valorMin'),
    })
  }
  if (filters.valorMax !== undefined) {
    chips.push({
      key: 'vmax', label: 'Max', value: `R$ ${filters.valorMax.toLocaleString('pt-BR')}`,
      onRemove: () => onClear('valorMax'),
    })
  }
  return chips
}

// ---------------------------------------------------------------------------
// URL state helpers — minimal, not framework-coupled. Mirrors filters/page/sort
// into the querystring so a refresh keeps the user's recorte.
//
// Dates are read/written as UTC YYYY-MM-DD strings via `toDateInputValue` /
// `parseDateInputValue` so a calendar day selected by the user stays the same
// calendar day everywhere — backend stores UTC midnight too.
// ---------------------------------------------------------------------------

interface UrlState {
  filters: GerencialFilterState
  page: number
  pageSize: number
  sort: SortState
}

function readUrlState(): Partial<UrlState> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const out: Partial<UrlState> = {}
  const di = params.get('di')
  const df = params.get('df')
  if (di && df) {
    const di_ = parseDateInputValue(di)
    const df_ = parseDateInputValue(df)
    if (di_ && df_) {
      out.filters = {
        dataInicial: di_,
        dataFinal: df_,
        codigoCliente: params.get('cli') ?? undefined,
        natureza: params.get('nat') ?? undefined,
        centroResultado: params.get('cr') ?? undefined,
        empresa: params.get('emp') ?? undefined,
        centroCusto: params.get('cc') ?? undefined,
        search: params.get('q') ?? undefined,
        valorMin: params.has('vmin') ? Number(params.get('vmin')) : undefined,
        valorMax: params.has('vmax') ? Number(params.get('vmax')) : undefined,
      }
    }
  }
  if (params.has('p')) out.page = Math.max(1, Number(params.get('p')) || 1)
  if (params.has('ps')) out.pageSize = Math.max(1, Number(params.get('ps')) || 30)
  if (params.has('sb') && params.has('sd')) {
    const sb = params.get('sb')
    const sd = params.get('sd')
    if ((sb === 'Data' || sb === 'Valor') && (sd === 'asc' || sd === 'desc')) {
      out.sort = { col: sb, dir: sd }
    }
  }
  return out
}

function writeUrlState(state: UrlState): void {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams()
  params.set('di', toDateInputValue(state.filters.dataInicial))
  params.set('df', toDateInputValue(state.filters.dataFinal))
  if (state.filters.codigoCliente) params.set('cli', state.filters.codigoCliente)
  if (state.filters.natureza) params.set('nat', state.filters.natureza)
  if (state.filters.centroResultado) params.set('cr', state.filters.centroResultado)
  if (state.filters.empresa) params.set('emp', state.filters.empresa)
  if (state.filters.centroCusto) params.set('cc', state.filters.centroCusto)
  if (state.filters.search) params.set('q', state.filters.search)
  if (state.filters.valorMin !== undefined) params.set('vmin', String(state.filters.valorMin))
  if (state.filters.valorMax !== undefined) params.set('vmax', String(state.filters.valorMax))
  params.set('p', String(state.page))
  params.set('ps', String(state.pageSize))
  params.set('sb', state.sort.col)
  params.set('sd', state.sort.dir)
  window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
}

// ---------------------------------------------------------------------------

export default function GerencialPage() {
  const { user } = useAuth()
  const { success: toastSuccess, error: toastError } = useToast()

  const initial = useMemo(() => {
    const url = readUrlState()
    const def = getDefaultDateRange()
    return {
      filters: url.filters ?? { dataInicial: def.dataInicial, dataFinal: def.dataFinal },
      page: url.page ?? 1,
      pageSize: url.pageSize ?? 30,
      sort: url.sort ?? { col: 'Data' as const, dir: 'desc' as const },
    }
  }, [])

  const [filters, setFilters] = useState<GerencialFilterState>(initial.filters)
  const [page, setPage] = useState(initial.page)
  const [pageSize, setPageSize] = useState(initial.pageSize)
  const [sort, setSort] = useState<SortState>(initial.sort)

  const [viewMode, setViewMode] = useState<'read' | 'edit'>('read')
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set())

  const [importOpen, setImportOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false)
  const [savingOpen, setSavingOpen] = useState(false)
  const [filterPending, setFilterPending] = useState<GerencialFilterState | null>(null)
  const [exporting, setExporting] = useState(false)

  const dirtyRows = useDirtyRows()

  // Persist to URL whenever inputs change (lightweight; no router coupling)
  useEffect(() => {
    writeUrlState({ filters, page, pageSize, sort })
  }, [filters, page, pageSize, sort])

  const reportQuery = useFluxoGerencialListReport({
    ...filters,
    page,
    pageSize,
    sortBy: sort.col,
    sortDir: sort.dir,
  } satisfies ListReportFilters)

  const clientsQuery = useFluxoGerencialClients({
    dataInicial: filters.dataInicial,
    dataFinal: filters.dataFinal,
  })

  // Export uses the unpaginated `getReport` endpoint. Disabled by default —
  // explicitly refetched only inside `handleExport`.
  const exportFetch = useFluxoGerencialReport({
    dataInicial: filters.dataInicial,
    dataFinal: filters.dataFinal,
    codigoCliente: filters.codigoCliente,
    enabled: false,
  })

  const batchMutation = useBatchUpdateFluxoGerencial()

  const items: GerencialRow[] = (reportQuery.data?.items ?? []) as GerencialRow[]
  const total = reportQuery.data?.total ?? 0
  const totalPages = reportQuery.data?.totalPages ?? 1

  const canEdit = !!user && EDIT_ROLES.includes(user.role)

  // Distinct values from the current page — feed dropdowns/datalists.
  // Good enough for v1; future could fetch full distinct sets from backend.
  const centroResultadoOptions = useMemo(() => {
    const set = new Set<string>()
    items.forEach(r => { if (r.CentroResultado) set.add(r.CentroResultado) })
    return Array.from(set).sort()
  }, [items])

  const naturezaSuggestions = useMemo(() => {
    const set = new Set<string>()
    items.forEach(r => { if (r.Natureza) set.add(r.Natureza) })
    return Array.from(set).sort()
  }, [items])

  // ---------------------- handlers ----------------------

  const applyFilters = useCallback((next: GerencialFilterState) => {
    setFilters(next)
    setPage(1)
    // Selection no longer maps to visible rows after a filter change.
    setSelectedRowIds(new Set())
  }, [])

  const handlePageChange = useCallback((next: number) => {
    setPage(next)
    setSelectedRowIds(new Set())
  }, [])

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next)
    setPage(1)
    setSelectedRowIds(new Set())
  }, [])

  const handleFiltersChange = useCallback((next: GerencialFilterState) => {
    if (dirtyRows.hasDirty) {
      setFilterPending(next)
    } else {
      applyFilters(next)
    }
  }, [dirtyRows.hasDirty, applyFilters])

  const handleClearFilter = useCallback((key: keyof GerencialFilterState) => {
    const next: GerencialFilterState = { ...filters }
    if (key === 'dataInicial' || key === 'dataFinal') return
    delete next[key]
    handleFiltersChange(next)
  }, [filters, handleFiltersChange])

  const handleClearAllFilters = useCallback(() => {
    const def = getDefaultDateRange()
    handleFiltersChange({ dataInicial: def.dataInicial, dataFinal: def.dataFinal })
  }, [handleFiltersChange])

  const handleLast90Days = useCallback(() => {
    // UTC-anchored — match backend storage convention.
    const today = new Date()
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))
    const from = new Date(todayUTC.getTime() - 90 * 86400000)
    handleFiltersChange({ dataInicial: from, dataFinal: todayUTC })
  }, [handleFiltersChange])

  const handleEnterEditMode = useCallback(() => {
    setViewMode('edit')
    setSelectedRowIds(new Set())
  }, [])

  const handleCancelEdit = useCallback(() => {
    if (dirtyRows.hasDirty) {
      const ok = window.confirm(
        `Você tem ${dirtyRows.changeCount} alterações não salvas. Sair perderá tudo. Continuar?`,
      )
      if (!ok) return
    }
    dirtyRows.reset()
    setSelectedRowIds(new Set())
    setViewMode('read')
  }, [dirtyRows])

  const handleSaveClick = useCallback(() => {
    setSaveConfirmOpen(true)
  }, [])

  const handleSaveConfirm = useCallback(async () => {
    // Defensive: if validation flipped errors on between click and confirm,
    // bail out instead of sending invalid rows to the backend.
    if (!dirtyRows.canSave || batchMutation.isPending) {
      setSaveConfirmOpen(false)
      return
    }
    setSaveConfirmOpen(false)
    setSavingOpen(true)
    try {
      const payload = dirtyRows.buildPayload()
      const updates = payload.updates.map(p => ({
        id: p.id,
        fields: serializeFields(p.fields),
      }))
      const deletes = payload.deletes
      if (updates.length === 0 && deletes.length === 0) {
        setSavingOpen(false)
        return
      }
      const result = await batchMutation.mutateAsync({ updates, deletes })
      const totalApplied = result.updatedCount + result.deletedCount
      if (result.failed.length > 0) {
        toastError(
          `${totalApplied} aplicadas · ${result.failed.length} falharam (IDs: ${result.failed.slice(0, 3).map(f => f.id).join(', ')}${result.failed.length > 3 ? '…' : ''})`,
        )
      } else {
        const parts: string[] = []
        if (result.updatedCount > 0) {
          parts.push(`${result.updatedCount} ${result.updatedCount === 1 ? 'atualizada' : 'atualizadas'}`)
        }
        if (result.deletedCount > 0) {
          parts.push(`${result.deletedCount} ${result.deletedCount === 1 ? 'excluída' : 'excluídas'}`)
        }
        toastSuccess(`${parts.join(' · ')} · DRE reprocessando`)
      }
      dirtyRows.reset()
      setSelectedRowIds(new Set())
      setViewMode('read')
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Erro ao salvar alterações')
    } finally {
      setSavingOpen(false)
    }
  }, [dirtyRows, batchMutation, toastSuccess, toastError])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      // exportFetch (getReport) returns ALL filtered rows (not paginated).
      // It only respects period + codigoCliente at the server level — the
      // remaining filters (natureza, search, ...) are applied client-side
      // for v1. Future: extend getReport to accept the same expanded filter
      // schema as listReport.
      const result = await exportFetch.refetch()
      let rows = result.data?.items ?? []
      if (filters.natureza) rows = rows.filter(r => r.Natureza === filters.natureza)
      if (filters.centroResultado) rows = rows.filter(r => r.CentroResultado === filters.centroResultado)
      if (filters.empresa) rows = rows.filter(r => (r.Empresa || '').toLowerCase().includes(filters.empresa!.toLowerCase()))
      if (filters.search) {
        const q = filters.search.toLowerCase()
        rows = rows.filter(r =>
          (r.Cliente || '').toLowerCase().includes(q) ||
          (r.Nota || '').toLowerCase().includes(q) ||
          (r.Job || '').toLowerCase().includes(q) ||
          (r.Empresa || '').toLowerCase().includes(q),
        )
      }
      if (filters.valorMin !== undefined) rows = rows.filter(r => (r.Valor ?? 0) >= filters.valorMin!)
      if (filters.valorMax !== undefined) rows = rows.filter(r => (r.Valor ?? 0) <= filters.valorMax!)

      await exportGerencialToExcel(rows.map(r => ({
        Id: r.Id,
        CodigoCliente: r.CodigoCliente,
        Cliente: r.Cliente,
        Data: r.Data,
        Valor: r.Valor,
        Natureza: r.Natureza,
        Nota: r.Nota,
        Job: r.Job,
      })))
      toastSuccess(`${rows.length} registro${rows.length === 1 ? '' : 's'} exportado${rows.length === 1 ? '' : 's'}`)
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Erro ao exportar')
    } finally {
      setExporting(false)
    }
  }, [exportFetch, filters, toastSuccess, toastError])

  // ---------------------- access control ----------------------
  if (!user || !ALLOWED_ROLES.includes(user.role)) {
    return <GerencialAccessRestricted />
  }

  const chips = buildChips(filters, clientsQuery.data ?? [], handleClearFilter)
  const isEdit = viewMode === 'edit'

  return (
    <div className="space-y-6 p-6">
      {isEdit ? (
        <GerencialEditToolbar
          updateCount={dirtyRows.updateCount}
          deleteCount={dirtyRows.deleteCount}
          errorCount={dirtyRows.errorCount}
          selectedCount={selectedRowIds.size}
          canSave={dirtyRows.canSave}
          onUndo={() => dirtyRows.undo()}
          onCancel={handleCancelEdit}
          onSave={handleSaveClick}
          onDeleteSelected={() => {
            dirtyRows.markForDelete(selectedRowIds)
            setSelectedRowIds(new Set())
          }}
        />
      ) : (
        <GerencialTopBar
          totalRecords={total}
          loading={reportQuery.isLoading}
          exporting={exporting}
          onExport={handleExport}
          onImport={() => setImportOpen(true)}
          onDownloadTemplate={() => downloadBlankTemplate()}
          onEnterEditMode={handleEnterEditMode}
          onOpenHistory={() => setHistoryOpen(true)}
          canEdit={canEdit}
        />
      )}

      <GerencialFiltersBar
        value={filters}
        onChange={handleFiltersChange}
        clients={clientsQuery.data ?? []}
        centroResultados={centroResultadoOptions}
        naturezaSuggestions={naturezaSuggestions}
        compact={isEdit}
      />

      {!isEdit && chips.length > 0 && (
        <GerencialFilterChips chips={chips} onClearAll={handleClearAllFilters} />
      )}

      {!reportQuery.isLoading && items.length === 0 ? (
        <GerencialEmptyState
          onLast90Days={handleLast90Days}
          onClearFilters={handleClearAllFilters}
          onImport={() => setImportOpen(true)}
        />
      ) : (
        <GerencialTable
          data={items}
          loading={reportQuery.isLoading}
          mode={viewMode}
          sort={sort}
          onSortChange={setSort}
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          dirty={dirtyRows.dirty}
          errors={dirtyRows.errors}
          onCellChange={dirtyRows.setCell}
          onValidationChange={dirtyRows.setError}
          selectedRowIds={selectedRowIds}
          onSelectionChange={setSelectedRowIds}
          pendingDeletes={isEdit ? dirtyRows.pendingDeletes : undefined}
          onToggleDelete={isEdit ? dirtyRows.toggleDelete : undefined}
        />
      )}

      <GerencialImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onDownloadTemplate={() => downloadBlankTemplate()}
        onImportComplete={() => reportQuery.refetch()}
      />

      <AuditHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />

      <GerencialSaveConfirm
        open={saveConfirmOpen}
        onOpenChange={setSaveConfirmOpen}
        dirty={dirtyRows.dirty}
        pendingDeletes={dirtyRows.pendingDeletes}
        onConfirm={handleSaveConfirm}
      />

      <GerencialSavingDialog open={savingOpen} dirtyCount={dirtyRows.changeCount} />

      {/* Filter change with dirty rows — confirm modal */}
      <Dialog open={!!filterPending} onOpenChange={(o) => !o && setFilterPending(null)}>
        <DialogContent className="bg-brand-dark border-brand-dark-lighter max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-display">
              Alterações não salvas
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white">
            Você tem {dirtyRows.changeCount} linha{dirtyRows.changeCount === 1 ? '' : 's'} alterada{dirtyRows.changeCount === 1 ? '' : 's'}.
            Mudar o filtro descartará essas alterações.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFilterPending(null)}>
              Manter alterações
            </Button>
            <Button variant="primary" onClick={() => {
              if (filterPending) {
                dirtyRows.reset()
                applyFilters(filterPending)
              }
              setFilterPending(null)
            }}>
              Descartar e filtrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Maps `useDirtyRows` raw values (CellValue: string | number | Date | null)
 * to backend `BatchUpdateFields` shape (Date stays Date, numbers stay numbers,
 * strings/null stay as-is). Only fields actually present in the patch are sent.
 */
function serializeFields(fields: Partial<Record<EditableField, CellValue>>): BatchUpdateFields {
  const out: BatchUpdateFields = {}
  for (const [k, v] of Object.entries(fields)) {
    const key = k as EditableField
    if (v === undefined) continue
    switch (key) {
      case 'Data': {
        out.Data = v instanceof Date ? v : new Date(String(v))
        break
      }
      case 'Valor': {
        out.Valor = typeof v === 'number' ? v : Number(v)
        break
      }
      case 'CentroResultado': {
        out.CentroResultado = v === null ? null : (typeof v === 'number' ? v : Number(v))
        break
      }
      case 'NumeroNota':
      case 'Natureza':
      case 'Empresa':
      case 'Descricao':
        out[key] = v === null ? null : String(v)
        break
      case 'CentroCusto':
        out.CentroCusto = v === null ? '' : String(v)
        break
    }
  }
  return out
}

